import React, { forwardRef, useMemo } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import type { SwiperProps } from "swiper/react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Keyboard, Mousewheel, Navigation, Pagination } from "swiper/modules";

export interface SwipperProps {
    list: {
        key?: string;
        node: React.ReactNode;
    }[];
    className?: string;
    cssMode?: boolean;
    loop?: boolean;
    autoplay?: boolean | { delay?: number; disableOnInteraction?: boolean; pauseOnMouseEnter?: boolean };
    slidesPerView?: SwiperProps["slidesPerView"];
    spaceBetween?: number;
    breakpoints?: SwiperProps["breakpoints"];
    navigation?: boolean;
    pagination?: boolean | { clickable?: boolean };
    mousewheel?: boolean;
    keyboard?: boolean;
    paginationRef?: React.RefObject<HTMLDivElement>;
    onSwiper?: (swiper: SwiperInstance) => void;
}

const Swipper = forwardRef<HTMLDivElement, SwipperProps>((props: SwipperProps) => {
    const {
        list,
        className,
        cssMode = true,
        loop = false,
        autoplay,
        slidesPerView = 1,
        spaceBetween = 0,
        breakpoints,
        navigation = true,
        pagination = true,
        mousewheel = true,
        keyboard = true,
        paginationRef,
        onSwiper
    } = props;

    const modules = useMemo(() => {
        const enabledModules = [];

        if (navigation) enabledModules.push(Navigation);
        if (pagination) enabledModules.push(Pagination);
        if (mousewheel) enabledModules.push(Mousewheel);
        if (keyboard) enabledModules.push(Keyboard);
        if (autoplay) enabledModules.push(Autoplay);

        return enabledModules;
    }, [autoplay, keyboard, mousewheel, navigation, pagination]);

    return (
        <Swiper
            className={className}
            cssMode={cssMode}
            loop={loop}
            autoplay={autoplay === true ? { delay: 5000, pauseOnMouseEnter: true } : autoplay || undefined}
            slidesPerView={slidesPerView}
            spaceBetween={spaceBetween}
            breakpoints={breakpoints}
            navigation={navigation}
            pagination={pagination === true ? { clickable: true } : pagination || undefined}
            mousewheel={mousewheel}
            keyboard={keyboard}
            modules={modules}
            onBeforeInit={(swiper: SwiperInstance) => {
                if (paginationRef?.current && swiper.params.pagination) {
                    const pag = swiper.params.pagination;
                    if (typeof pag !== "boolean") {
                        pag.el = paginationRef.current;
                    }
                }
            }}
            onSwiper={(swiper: SwiperInstance) => {
                onSwiper?.(swiper);
            }}
        >
            {list?.map((obj, index) => (
                <SwiperSlide key={obj.key ?? index}>{obj.node}</SwiperSlide>
            ))}
        </Swiper>
    );
});

Swipper.displayName = "Swipper";

export default Swipper;
